import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Stack,
  TablePagination,
  Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TuneIcon from '@mui/icons-material/Tune';
import { toast } from 'react-toastify';
import { leaveApi } from './leaveApi';

export default function LeaveLedgerExplorer() {
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedType, setSelectedType] = useState('');
  const [txFilter, setTxFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalCount, setTotalCount] = useState(0);

  // Manual Adjustment Modal
  const [openAdjustModal, setOpenAdjustModal] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    employee_id: '',
    leave_type_id: '',
    days_delta: '',
    reason: '',
  });
  const [adjusting, setAdjusting] = useState(false);

  const fetchLedger = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedType) params.append('leave_type_id', selectedType);
      if (txFilter) params.append('transaction_type', txFilter);
      params.append('page', page + 1);
      params.append('page_size', rowsPerPage);

      const [ledgerData, typesData] = await Promise.all([
        leaveApi.getLedger(params.toString()),
        leaveApi.getTypes(),
      ]);

      setLedgerEntries(ledgerData.results || ledgerData);
      setTotalCount(ledgerData.count || ledgerData.length || 0);
      setTypes(typesData);
    } catch (err) {
      toast.error('Failed to load ledger records.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, txFilter, page, rowsPerPage]);

  const handleManualAdjust = async () => {
    if (!adjustForm.employee_id || !adjustForm.leave_type_id || !adjustForm.days_delta || !adjustForm.reason) {
      toast.warning('Please fill all adjustment fields.');
      return;
    }
    try {
      setAdjusting(true);
      await leaveApi.adjustBalance(adjustForm);
      toast.success('Manual balance adjustment recorded in immutable ledger.');
      setOpenAdjustModal(false);
      setAdjustForm({ employee_id: '', leave_type_id: '', days_delta: '', reason: '' });
      fetchLedger();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to record adjustment.';
      toast.error(msg);
    } finally {
      setAdjusting(false);
    }
  };

  const txTypeBadges = {
    OPENING_BALANCE: { label: 'Opening Balance', color: 'info' },
    MONTHLY_ACCRUAL: { label: 'Monthly Accrual', color: 'success' },
    LEAVE_RESERVE_HOLD: { label: 'Reservation Hold', color: 'warning' },
    LEAVE_UTILIZE_CONFIRM: { label: 'Utilize Confirmed', color: 'primary' },
    LEAVE_RELEASE_REFUND: { label: 'Refund / Cancel', color: 'secondary' },
    YEAR_END_CARRY_FORWARD: { label: 'Carry Forward', color: 'info' },
    YEAR_END_LAPSE: { label: 'Year-End Lapse', color: 'error' },
    ENCASHMENT: { label: 'Encashment Payout', color: 'default' },
    MANUAL_ADJUSTMENT: { label: 'Manual Adjustment', color: 'warning' },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header & Metric Cards */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
            Immutable Leave Transaction Ledger
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Append-only audit trail protected against update/deletion with double-entry balance accounting.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            icon={<CheckCircleOutlineIcon />}
            label="Conservation Invariant Active (100% In Sync)"
            color="success"
            sx={{ fontWeight: 700 }}
          />
          <Button
            variant="contained"
            startIcon={<TuneIcon />}
            onClick={() => setOpenAdjustModal(true)}
            sx={{ borderRadius: '12px', bgcolor: '#2563eb', textTransform: 'none', fontWeight: 700 }}
          >
            Record Manual Adjustment
          </Button>
        </Stack>
      </Box>

      {/* Filter Bar */}
      <Paper sx={{ p: 2, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Leave Type</InputLabel>
            <Select
              value={selectedType}
              label="Filter by Leave Type"
              onChange={(e) => { setSelectedType(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All Leave Types</MenuItem>
              {types.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name} ({t.code})</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Filter by Transaction Type</InputLabel>
            <Select
              value={txFilter}
              label="Filter by Transaction Type"
              onChange={(e) => { setTxFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All Transaction Types</MenuItem>
              <MenuItem value="OPENING_BALANCE">Opening Balance</MenuItem>
              <MenuItem value="MONTHLY_ACCRUAL">Monthly Accrual</MenuItem>
              <MenuItem value="LEAVE_RESERVE_HOLD">Reservation Hold</MenuItem>
              <MenuItem value="LEAVE_UTILIZE_CONFIRM">Utilization Confirm</MenuItem>
              <MenuItem value="LEAVE_RELEASE_REFUND">Release / Refund</MenuItem>
              <MenuItem value="MANUAL_ADJUSTMENT">Manual Adjustment</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Ledger Table */}
      <TableContainer component={Paper} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Date & Time</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Leave Type</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Transaction Type</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, color: '#475569' }}>Delta (Days)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, color: '#475569' }}>Available After</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Audit Reason</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Actor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : ledgerEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#64748b' }}>
                  No transaction ledger entries recorded yet.
                </TableCell>
              </TableRow>
            ) : (
              ledgerEntries.map((row) => {
                const badge = txTypeBadges[row.transaction_type] || { label: row.transaction_type, color: 'default' };
                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ color: '#475569', fontSize: '0.85rem' }}>
                      {new Date(row.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {row.employee_name} ({row.employee_no})
                    </TableCell>
                    <TableCell>
                      <Chip label={row.leave_type_code} size="small" sx={{ fontWeight: 700 }} />
                    </TableCell>
                    <TableCell>
                      <Chip label={badge.label} color={badge.color} size="small" sx={{ fontWeight: 700 }} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                      {row.days}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: '#059669' }}>
                      {row.balance_available_after}
                    </TableCell>
                    <TableCell sx={{ color: '#64748b', maxWidth: 260 }}>
                      {row.reason || '—'}
                    </TableCell>
                    <TableCell sx={{ color: '#475569', fontSize: '0.85rem' }}>
                      {row.actor_name || 'System'}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[15, 30, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>

      {/* Manual Adjustment Dialog */}
      <Dialog open={openAdjustModal} onClose={() => setOpenAdjustModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Record Manual Balance Adjustment</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          <Alert severity="info" sx={{ borderRadius: '12px' }}>
            Manual adjustments directly mutate entitlement and available balance, and are permanently recorded in the immutable audit ledger.
          </Alert>

          <TextField
            fullWidth
            size="small"
            type="number"
            label="Employee ID"
            value={adjustForm.employee_id}
            onChange={(e) => setAdjustForm({ ...adjustForm, employee_id: e.target.value })}
            helperText="Internal database ID of the target employee"
          />

          <FormControl fullWidth size="small">
            <InputLabel>Leave Type</InputLabel>
            <Select
              value={adjustForm.leave_type_id}
              label="Leave Type"
              onChange={(e) => setAdjustForm({ ...adjustForm, leave_type_id: e.target.value })}
            >
              {types.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name} ({t.code})</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            type="number"
            label="Adjustment Quantity (Days)"
            value={adjustForm.days_delta}
            onChange={(e) => setAdjustForm({ ...adjustForm, days_delta: e.target.value })}
            helperText="Positive value to credit entitlement (e.g. 2.00 or 5.00)"
          />

          <TextField
            fullWidth
            size="small"
            multiline
            rows={3}
            label="Mandatory Audit Justification"
            value={adjustForm.reason}
            onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
            placeholder="e.g. Discretionary sign-on credit approved by HR VP"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenAdjustModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleManualAdjust}
            disabled={adjusting}
            startIcon={adjusting ? <CircularProgress size={16} /> : null}
          >
            {adjusting ? 'Recording...' : 'Commit Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
